import { useState, useEffect, useRef } from "react";
import { Lock, Upload, Trash2, LogOut, Image, Film, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const GalleryAdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [pin, setPin] = useState("");
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileRef = useRef(null);

  const handleLogin = async () => {
    try {
      const res = await axios.post(`${API}/gallery/login`, { pin });
      if (res.data.success) {
        setIsLoggedIn(true);
        localStorage.setItem("galleryPin", pin);
        toast.success("Welcome!");
        fetchGallery();
      } else {
        toast.error("Invalid PIN");
      }
    } catch {
      toast.error("Login failed");
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setPin("");
    localStorage.removeItem("galleryPin");
  };

  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      setItems(res.data);
    } catch {
      console.error("Failed to fetch gallery");
    }
  };

  useEffect(() => {
    const stored = localStorage.getItem("galleryPin");
    if (stored) {
      setPin(stored);
      setIsLoggedIn(true);
      fetchGallery();
    }
  }, []);

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    const storedPin = localStorage.getItem("galleryPin");
    let uploaded = 0;

    for (const file of selectedFiles) {
      const fd = new FormData();
      fd.append("media", file);
      fd.append("caption", caption);
      try {
        await axios.post(`${API}/gallery/upload?pin=${storedPin}`, fd);
        uploaded++;
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (uploaded > 0) {
      toast.success(`${uploaded} file${uploaded > 1 ? "s" : ""} uploaded!`);
    }
    setSelectedFiles([]);
    setCaption("");
    if (fileRef.current) fileRef.current.value = "";
    fetchGallery();
    setUploading(false);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm("Delete this item?")) return;
    const storedPin = localStorage.getItem("galleryPin");
    try {
      await axios.delete(`${API}/gallery/${itemId}?pin=${storedPin}`);
      toast.success("Deleted!");
      fetchGallery();
    } catch {
      toast.error("Failed to delete");
    }
  };

  // Login screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 shadow-lg max-w-sm w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-[#7dbff8]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Image className="w-8 h-8 text-[#7dbff8]" />
            </div>
            <h1 className="text-2xl font-bold text-[#2D3436]" style={{ fontFamily: 'Fredoka, cursive' }}>
              Gallery Login
            </h1>
            <p className="text-sm text-[#636E72] mt-1">Enter the gallery PIN to upload photos & videos</p>
          </div>
          <div className="space-y-4">
            <input
              type="password"
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              placeholder="••••"
              className="w-full text-center text-3xl tracking-[0.5em] p-4 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none"
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              data-testid="gallery-pin-input"
            />
            <button
              onClick={handleLogin}
              className="w-full bg-[#7dbff8] hover:bg-[#6ca6d9] text-white py-3 rounded-full font-bold transition-colors"
              style={{ fontFamily: 'Fredoka, cursive' }}
              data-testid="gallery-login-btn"
            >
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="gallery-admin-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-4 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Image className="w-6 h-6 text-[#7dbff8]" />
            <h1 className="text-xl font-bold text-[#2D3436]" style={{ fontFamily: 'Fredoka, cursive' }}>
              Gallery Manager
            </h1>
            <span className="text-xs bg-[#7dbff8]/10 text-[#7dbff8] px-2 py-1 rounded-full font-semibold">{items.length} items</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/gallery" className="text-sm text-[#7dbff8] font-semibold hover:underline">View Gallery</a>
            <button onClick={handleLogout} className="flex items-center gap-1 text-sm text-[#636E72] hover:text-[#ff3131]" data-testid="gallery-logout-btn">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Upload Area */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 mb-6">
          <h2 className="text-lg font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>Upload Photos & Videos</h2>
          
          <div className="space-y-4">
            <div>
              <label
                className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-[#7dbff8] transition-colors bg-[#FFFBF5]"
                data-testid="gallery-upload-area"
              >
                <Upload className="w-10 h-10 text-[#636E72] mb-2" />
                <span className="text-sm text-[#636E72] font-medium">Click to select photos or videos</span>
                <span className="text-xs text-[#636E72]/60 mt-1">Supports JPG, PNG, WEBP, MP4, MOV</span>
                <input
                  ref={fileRef}
                  type="file"
                  accept="*/*"
                  multiple
                  onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                  className="hidden"
                  data-testid="gallery-file-input"
                />
              </label>
              {selectedFiles.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 bg-[#7dbff8]/10 px-3 py-1.5 rounded-full text-xs text-[#2D3436]">
                      {f.type.startsWith("video") ? <Film className="w-3 h-3 text-[#7dbff8]" /> : <Image className="w-3 h-3 text-[#7dbff8]" />}
                      {f.name.length > 25 ? f.name.slice(0, 22) + "..." : f.name}
                      <button onClick={() => setSelectedFiles(prev => prev.filter((_, j) => j !== i))} className="text-[#ff3131] hover:text-[#e62e2e]">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#2D3436] mb-1">Caption <span className="text-xs font-normal text-[#636E72]">(optional, applies to all)</span></label>
              <input
                type="text"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="e.g. Tuesday class at Hamble"
                className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm"
                data-testid="gallery-caption-input"
              />
            </div>

            <button
              onClick={handleUpload}
              disabled={selectedFiles.length === 0 || uploading}
              className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-6 py-3 rounded-full font-bold transition-colors disabled:opacity-40 flex items-center gap-2"
              style={{ fontFamily: 'Fredoka, cursive' }}
              data-testid="gallery-upload-btn"
            >
              {uploading ? (
                <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Uploading...</>
              ) : (
                <><Upload className="w-4 h-4" /> Upload {selectedFiles.length > 0 ? `${selectedFiles.length} File${selectedFiles.length > 1 ? "s" : ""}` : ""}</>
              )}
            </button>
          </div>
        </div>

        {/* Existing Items */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100">
          <h2 className="text-lg font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>Gallery Items ({items.length})</h2>
          
          {items.length === 0 ? (
            <div className="text-center py-12">
              <Image className="w-16 h-16 text-[#636E72]/20 mx-auto mb-3" />
              <p className="text-[#636E72] text-sm">No items yet. Upload your first photo or video above!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {items.map((item) => (
                <div key={item.id} className="relative group rounded-xl overflow-hidden bg-gray-100 aspect-square" data-testid={`gallery-admin-item-${item.id}`}>
                  {item.media_type === "video" ? (
                    <video src={`${item.url}`} className="w-full h-full object-cover" muted preload="metadata" />
                  ) : (
                    <img src={`${item.url}`} alt={item.caption || ""} className="w-full h-full object-cover" loading="lazy" />
                  )}
                  {item.media_type === "video" && (
                    <div className="absolute top-2 left-2">
                      <Film className="w-4 h-4 text-white drop-shadow" />
                    </div>
                  )}
                  {item.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                      <p className="text-white text-[10px] truncate">{item.caption}</p>
                    </div>
                  )}
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="absolute top-2 right-2 w-7 h-7 bg-[#ff3131] text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#e62e2e]"
                    data-testid={`gallery-delete-${item.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GalleryAdminPage;
